import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useWebMcpTools from "../hooks/useWebMcpTools";

const COLLECTION_TYPES = [
  "recommended-room",
  "color-psychology-primary-trait",
  "color-psychology-impact-level",
  "color-category",
  "keywords",
];

const textResult = (text) => ({ content: [{ type: "text", text }] });

// Site-wide navigation only. Tools that read or change page state are owned by
// the relevant page and are registered only while that state is available.
const WebMcpTools = () => {
  const navigate = useNavigate();

  const tools = useMemo(
    () => [
      {
        name: "browse_prints",
        description: "Open the public gallery of AI-generated art prints.",
        inputSchema: {
          type: "object",
          properties: {},
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: async () => {
          navigate("/prints");
          return textResult("Opened the public print gallery.");
        },
      },
      {
        name: "browse_collection",
        description: "Open a public print collection by category and value.",
        inputSchema: {
          type: "object",
          properties: {
            collectionType: { type: "string", enum: COLLECTION_TYPES },
            value: {
              type: "string",
              description: "Optional collection value, such as living-room or serene.",
              maxLength: 100,
            },
          },
          required: ["collectionType"],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: async ({ collectionType, value }) => {
          if (!COLLECTION_TYPES.includes(collectionType)) {
            throw new Error("Unsupported collection type.");
          }
          const cleanValue = String(value || "").trim();
          const path = cleanValue
            ? `/collections/${collectionType}/${encodeURIComponent(cleanValue)}`
            : `/collections/${collectionType}`;
          navigate(path);
          return textResult(`Opened the ${cleanValue || collectionType} collection.`);
        },
      },
      {
        name: "open_print",
        description: "Open a print detail page using an artwork ID from the gallery.",
        inputSchema: {
          type: "object",
          properties: {
            id: {
              type: "string",
              description: "Artwork ID returned by a Create Prints gallery tool.",
              minLength: 1,
              maxLength: 100,
            },
          },
          required: ["id"],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: async ({ id }) => {
          const cleanId = String(id || "").trim();
          if (!cleanId || cleanId.includes("/")) throw new Error("Invalid artwork ID.");
          navigate(`/print/artwork/${encodeURIComponent(cleanId)}`);
          return textResult("Opened the print detail page.");
        },
      },
      {
        name: "start_print_creation",
        description: "Open the artwork studio, where the user can review a brief before sending it.",
        inputSchema: {
          type: "object",
          properties: {},
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: async () => {
          navigate("/generate-print");
          return textResult("Opened the artwork studio.");
        },
      },
    ],
    [navigate]
  );

  useWebMcpTools(tools);
  return null;
};

export default WebMcpTools;
